<template>
    <div
        id="wallettablecontainer"
        class="h-auto overflow-x-auto overflow-y-visible w-full border border-slate-200 dark:border-slate-700 rounded-lg shadow-md dark:shadow-slate-900/50 bg-white dark:bg-slate-800 wallet-table-container relative"
        @scroll="handleScroll"
    >
        <div class="min-w-max relative">
            <!-- Header Row -->
            <DraggableColumnHeaders
                v-if="filteredCards.length > 0"
                :isScrolledLeft="isScrolledLeft"
                :gridStyle="gridStyle"
                @sort="handleSort"
            />
            <!-- Empty state -->
            <div v-if="visibleCards.length === 0" class="bg-white dark:bg-slate-800 rounded-b-lg">
                <div>
                    <div class="py-8">
                        <EmptyState
                            v-if="hasActiveFilters"
                            title="No cards found matching filter criteria"
                            description="Try changing your filter selections."
                            :hasAction="true"
                            action-label="Clear filters"
                            @action="clearAllFilters"
                        />
                        <EmptyState
                            v-else
                            title="No cards found!"
                            description="Start adding cards to your wallet to see everything in one place."
                            :hasAction="true"
                            action-label="Add an account"
                            @action="handleAddAccount"
                        />
                    </div>
                </div>
            </div>

            <!-- Card list -->
            <!-- Cards, no grouping or subgrouping: -->
            <div
                v-else-if="!walletStore.groupBy"
                class="bg-white dark:bg-slate-800 grow shrink-0 w-full h-auto overflow-y-visible"
                :key="`wallet-cards-${groupSameCards}`"
            >
                <div
                    v-for="(group, index) in visibleCards"
                    :key="
                        groupSameCards
                            ? group.card_id || `unmatched-${group.id}`
                            : group.id
                    "
                >
                    <template v-if="groupSameCards">
                        <WalletTableGroupedRow
                            :index="index"
                            :group="group"
                            @edit="handleEdit"
                            @delete="handleDelete"
                            @view-details="handleViewDetails"
                            :gridStyle="gridStyle"
                            :totalCards="visibleCards.length"
                        />
                    </template>
                    <template v-else>
                        <WalletTableSingleRow
                            :key="group.id"
                            :card="group"
                            :index="index"
                            :isAGroup="false"
                            :gridStyle="gridStyle"
                            @edit="handleEdit"
                            @delete="handleDelete"
                            @view-details="handleViewDetails"
                        />
                    </template>
                </div>
            </div>
            <!-- Grouped cards: -->
            <div
                v-else
                class="grow shrink-0 w-full sticky left-0 top-0 h-auto overflow-y-visible"
                :key="`grouped-cards-${groupSameCards}`"
            >
                <!-- Grouped but not subgrouped cards: -->
                <template v-if="!walletStore.subgroupBy">
                    <template
                        v-for="(groupCards, groupName, index) in groupedCards"
                        :key="groupName"
                    >
                        <div
                            class="group-header grid bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-300 self-stretch py-3 border-b"
                            style="grid-template-columns: 500px"
                        >
                            <div
                                class="sticky left-0 z-10 bg-slate-50 dark:bg-slate-900 pl-6 flex items-center gap-2"
                            >
                                <WalletTableInstitutionTitle
                                    v-if="
                                        walletStore.groupBy.id ===
                                        'institution_name'
                                    "
                                    :item="groupCards"
                                    :name="groupName"
                                    :groupSameCards="groupSameCards"
                                />
                                <span v-else>
                                    {{ formatEntityName(groupName) }}
                                </span>
                                <span class="text-slate-500 dark:text-slate-400">
                                    ({{ groupCards.length }} card{{
                                        groupCards.length > 1 ? 's' : ''
                                    }})
                                </span>
                            </div>
                        </div>
                        <div
                            v-for="(group, index) in groupCards"
                            :key="
                                groupSameCards
                                    ? group.card_id || `unmatched-${group.id}`
                                    : group.id
                            "
                        >
                            <template v-if="groupSameCards">
                                <WalletTableGroupedRow
                                    :group="group"
                                    @edit="handleEdit"
                                    @delete="handleDelete"
                                    @view-details="handleViewDetails"
                                    :gridStyle="gridStyle"
                                    :index="index"
                                    :totalCards="groupCards.length"
                                />
                            </template>
                            <template v-else>
                                <WalletTableSingleRow
                                    :key="group.id"
                                    :card="group"
                                    :index="index"
                                    :isAGroup="false"
                                    :gridStyle="gridStyle"
                                    @edit="handleEdit"
                                    @delete="handleDelete"
                                    @view-details="handleViewDetails"
                                />
                            </template>
                        </div>
                    </template>
                </template>
                <!-- Grouped + subgrouped cards: -->
                <template v-else>
                    <!-- Primary groups (first level) -->
                    <template
                        v-for="(
                            subgroupsObj, primaryGroupName, index
                        ) in groupedCards"
                        :key="primaryGroupName"
                    >
                        <!-- Primary group header -->
                        <div
                            class="group-header grid bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-300 self-stretch py-3 border-b"
                            style="grid-template-columns: 500px"
                        >
                            <div
                                class="sticky left-0 z-10 bg-slate-50 dark:bg-slate-900 pl-6 flex items-center gap-2"
                            >
                                {{ formatEntityName(primaryGroupName) }}
                            </div>
                        </div>

                        <!-- Subgroups (second level) -->
                        <template
                            v-for="(
                                subgroupCards, subgroupName
                            ) in subgroupsObj"
                            :key="`${primaryGroupName}-${subgroupName}`"
                        >
                            <!-- Subgroup header -->
                            <div
                                class="subgroup-header grid bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-300 self-stretch py-3"
                                style="grid-template-columns: 500px"
                            >
                                <div
                                    class="flex items-center gap-2 sticky left-0 bg-slate-50 dark:bg-slate-900 pl-6"
                                >
                                    <WalletTableInstitutionTitle
                                        v-if="
                                            walletStore.subgroupBy.id ===
                                            'institution_name'
                                        "
                                        :item="subgroupCards"
                                        :name="subgroupName"
                                        :groupSameCards="groupSameCards"
                                    />
                                    <span v-else>
                                        {{ formatEntityName(subgroupName) }}
                                    </span>
                                    <span class="text-slate-500 dark:text-slate-400">
                                        ({{ subgroupCards.length }} card{{
                                            subgroupCards.length > 1 ? 's' : ''
                                        }})
                                    </span>
                                </div>
                            </div>

                            <!-- Subgroup cards -->
                            <div
                                v-for="(group, index) in subgroupCards"
                                :key="
                                    groupSameCards
                                        ? group.card_id ||
                                          `unmatched-${group.id}`
                                        : group.id
                                "
                            >
                                <template v-if="groupSameCards">
                                    <WalletTableGroupedRow
                                        :group="group"
                                        @edit="handleEdit"
                                        @delete="handleDelete"
                                        @view-details="handleViewDetails"
                                        :gridStyle="gridStyle"
                                        :totalCards="group.cards.length"
                                        :index="index"
                                    />
                                </template>
                                <template v-else>
                                    <WalletTableSingleRow
                                        :key="group.id"
                                        :card="group"
                                        :index="index"
                                        :isAGroup="false"
                                        :gridStyle="gridStyle"
                                        @edit="handleEdit"
                                        @delete="handleDelete"
                                        @view-details="handleViewDetails"
                                    />
                                </template>
                            </div>
                        </template>
                    </template>
                </template>
            </div>
        </div>
    </div>
    <div
        class="flex items-center justify-center text-sm pb-2 rounded-b-lg"
        v-show="hiddenCardsCount > 0 && visibleCards.length > 0"
    >
        <div
            class="rounded-b-lg bg-slate-50 dark:bg-slate-900 w-full py-2.5 px-2 border-b border-x border-slate-200 dark:border-slate-700 h-8 flex items-center justify-start"
        >
            <Transition name="fade" mode="out-in">
                <button
                    v-if="!showHiddenCards"
                    @click.prevent="setShowHiddenCards(true)"
                    class="flex gap-2 font-medium btn-transparent !py-1 !text-slate-500 min-w-[200px] justify-center"
                    role="button"
                    id="hs-wallet-show-hidden-cards"
                >
                    <Icon icon="eye-on" class="h-5 w-5" />
                    <span>
                        Show {{ hiddenCardsCount }} hidden card{{
                            hiddenCardsCount > 1 ? 's' : ''
                        }}
                    </span>
                </button>
                <button
                    v-else
                    @click.prevent="setShowHiddenCards(false)"
                    class="flex gap-2 font-medium btn-transparent !py-1 !text-slate-500 min-w-[200px] justify-center"
                    role="button"
                    id="hs-wallet-hide-hidden-cards"
                >
                    <Icon icon="eye-off" class="h-5 w-5" />
                    <span>Collapse hidden cards</span>
                </button>
            </Transition>
        </div>
    </div>
    <AccountSidebar :user="selectedUser" />
</template>
<script setup>
import { ref, watch, computed, provide } from 'vue';
import { useWalletStore } from '@/store/wallet';
import { storeToRefs } from 'pinia';
import AccountSidebar from '@/components/wallet/account-sidebar.vue';
import WalletTableSingleRow from '@/components/wallet/wallet-table-single-row.vue';
import WalletTableGroupedRow from '@/components/wallet/wallet-table-grouped-row.vue';
import Icon from '@/components/ui/icon/icon.vue';
import WalletTableInstitutionTitle from '@/components/wallet/wallet-table-institution-title.vue';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import DraggableColumnHeaders from '@/components/wallet/partials/draggable-column-headers.vue';
import { formatEntityName } from '@/utils/entityNameFormatter.js';
const emit = defineEmits(['edit', 'delete', 'view-details', 'add-account']);

const walletStore = useWalletStore();

const isScrolledLeft = ref(false);
const isScrolledRight = ref(false);

provide('isScrolledLeft', isScrolledLeft);
provide('isScrolledRight', isScrolledRight);

const handleScroll = (event) => {
    const container = event.target;

    isScrolledLeft.value = container.scrollLeft > 0;

    const isAtRightEdge =
        container.scrollWidth - container.clientWidth - container.scrollLeft <
        5;
    isScrolledRight.value = !isAtRightEdge;
};
const {
    orderedColumns,
    visibleColumns,
    groupSameCards,
    hasActiveFilters,
    showHiddenCards,
    visibleCards,
    groupedCards,
    filteredCards,
    hiddenCardsCount,
    selectedUser,
} = storeToRefs(walletStore);

const { clearAllFilters, setShowHiddenCards, setSortBy, setSubgroupBy } =
    walletStore;

const gridStyle = computed(() => {
    const visible = orderedColumns.value.filter((c) => isColumnVisible(c.id));
    const columnWidths = visible.map((col, index) => {
        if (index === 0 && col.id === 'title') {
            return '320px';
        }
        return col.width || 'auto';
    });

    return {
        gridTemplateColumns: columnWidths.join('  ') + ' 60px',
    };
});

watch(
    () => walletStore.groupBy,
    () => {
        setSubgroupBy(null);
    }
);

const isColumnVisible = (columnName) => {
    return visibleColumns.value.includes(columnName);
};

const handleEdit = (card) => {
    emit('edit', card);
};

const handleDelete = (card) => {
    emit('delete', card);
};

const handleViewDetails = (card) => {
    emit('view-details', card);
};

const handleSort = (direction, column) => {
    setSortBy(direction, column);
};

const handleAddAccount = () => {
    emit('add-account');
};
</script>

<style scoped>
/* Button transition effects */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
